import DOMPurify from "dompurify";
import { ChevronDown, ChevronUp, PlusCircle, Trash2 } from "lucide-react";
import RichResponseEditor from "./RichResponseEditor";

const RESPONSE_TYPES = [
  { value: "text", label: "Text Only" },
  { value: "quick", label: "Quick Replies" },
];



const ResponsesTab = ({
  responses,
  setResponses,
  onSelectQuickResponse,
}) => {

  // const safeResponses = Array.isArray(responses) ? responses : [];
  // console.log("safeResponses", safeResponses);




  /* ---------- HELPERS ---------- */
  const normalizePriority = (list) =>
    list.map((r, i) => ({ ...r, priority: i + 1 }));

  const updateResponse = (id, key, value) => {
    setResponses(prev =>
      prev.map(r =>
        r.id === id ? { ...r, [key]: value } : r
      )
    );
  };

  const addResponse = () => {
    setResponses(prev =>
      normalizePriority([
        ...prev,
        {
          id: Date.now() + "-" + Math.random().toString(36).slice(2),
          content: "",
          type: "text",
          preview: false,
          priority: prev.length + 1,
          quickReplies: [],
          variants: [],
        },
      ])
    );
  };

  const addVariant = (responseId) => {
    setResponses(prev =>
      prev.map(r =>
        r.id === responseId
          ? { ...r, variants: [...(r.variants || []), ""] }
          : r
      )
    );
  };

  const updateVariant = (responseId, variantIndex, value) => {
    setResponses(prev =>
      prev.map(r =>
        r.id === responseId
          ? { ...r, variants: (r.variants || []).map((v, i) => i === variantIndex ? value : v) }
          : r
      )
    );
  };

  const removeVariant = (responseId, variantIndex) => {
    setResponses(prev =>
      prev.map(r =>
        r.id === responseId
          ? { ...r, variants: (r.variants || []).filter((_, i) => i !== variantIndex) }
          : r
      )
    );
  };


  const deleteResponse = (id) => {
    setResponses(prev =>
      normalizePriority(prev.filter(r => r.id !== id))
    );
  };

  const moveResponse = (index, direction) => {
    setResponses(prev => {
      const updated = [...prev];
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= updated.length) return prev;
      [updated[index], updated[target]] = [updated[target], updated[index]];
      return normalizePriority(updated);
    });
  };

  /* ---------- RENDER ---------- */
  return (
    <div className="d-flex gap-4">
      <div className="flex-grow-1">

        {/* HEADER */}
        <div className="d-flex justify-content-between mb-3">
          <h5 className="fw-semibold">Responses</h5>
          <button
            className="btn btn-sm btn-primary"
            onClick={addResponse}
          >
            <i className="bi bi-plus-lg me-1" /> Add Response
          </button>
        </div>

        {/* RESPONSES */}
        {responses.map((res, index) => (
          <div
            key={res.id}
            className="p-3 mb-3 border rounded shadow-sm bg-white"
          >
            {/* HEADER */}
            <div className="d-flex justify-content-between mb-2">
              <strong>Response #{index + 1}</strong>
              <div className="btn-group btn-group-sm">
                <button
                  className="btn btn-light"
                  onClick={() => moveResponse(index, "up")}
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  className="btn btn-light"
                  onClick={() => moveResponse(index, "down")}
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  className="btn btn-light text-danger"
                  onClick={() => deleteResponse(res.id)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            {/* TOOLBAR: response type + preview toggle (rich formatting lives
                inside RichResponseEditor) */}
            <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
              <select
                className="form-select form-select-sm w-auto"
                value={res.type}
                onChange={(e) => {
                  const newType = e.target.value;

                  updateResponse(res.id, "type", newType);

                  // 🔹 ADDITION: sync quick reply selection
                  if (newType === "quick") {
                    onSelectQuickResponse(res.id);
                  } else {
                    onSelectQuickResponse?.(null);
                  }
                }}
              >
                {RESPONSE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>

              <div className="form-check form-switch ms-auto">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={res.preview}
                  onChange={(e) =>
                    updateResponse(res.id, "preview", e.target.checked)
                  }
                />
                <label className="form-check-label small">
                  Preview
                </label>
              </div>
            </div>

            {/* EDITOR / PREVIEW */}
            {!res.preview ? (
              <RichResponseEditor
                value={res.content}
                onChange={(html) => updateResponse(res.id, "content", html)}
              />
            ) : (
              <div className="border rounded p-2 bg-light mb-2">
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(res.content || "<em>No content</em>"),
                  }}
                />
              </div>
            )}

            {/* FOOTER */}
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">Priority</small>
              <input
                className="form-control form-control-sm w-25"
                value={res.priority}
                disabled
              />
            </div>

            {/* RESPONSE VARIANTS */}
            <div className="mt-3">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <small className="text-muted fw-semibold">
                  Response Variants
                  <span className="ms-1 text-secondary fw-normal">(bot picks one randomly)</span>
                </small>
                <button
                  className="btn btn-sm btn-outline-secondary py-0 px-2"
                  onClick={() => addVariant(res.id)}
                >
                  <PlusCircle size={12} className="me-1" />Add Variant
                </button>
              </div>
              {(res.variants || []).map((v, vi) => (
                <div key={vi} className="d-flex gap-1 mb-1">
                  <input
                    className="form-control form-control-sm"
                    placeholder={`Variant ${vi + 1}`}
                    value={v}
                    onChange={(e) => updateVariant(res.id, vi, e.target.value)}
                  />
                  <button
                    className="btn btn-sm btn-outline-danger py-0 px-2"
                    onClick={() => removeVariant(res.id, vi)}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>

            {/* QUICK INFO */}
            {res.type === "quick" && (
              <div className="small text-primary mt-2">
                Quick replies linked to response ID:
                <code className="ms-1">{res.id}</code>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResponsesTab;
